import assumeType from '@helpers/assumeType';
import getWebFileLocation from '@helpers/getWebFileLocation';
import liteMode from '@helpers/liteMode';
import makeGoogleMapsUrl from '@helpers/makeGoogleMapsUrl';
import mediaSizes from '@helpers/mediaSizes';
import {Middleware} from '@helpers/middleware';
import tsNow from '@helpers/tsNow';
import {GeoPoint, MessageMedia} from '@layer';
import I18n, {i18n, LangPackKey, FormatterArguments} from '@lib/langPack';
import setBlankToAnchor from '@lib/richTextProcessor/setBlankToAnchor';
import wrapEmojiText from '@lib/richTextProcessor/wrapEmojiText';
import {avatarNew} from '@components/avatarNew';
import GeoPin from '@components/geoPin';
import wrapPhoto from '@components/wrappers/photo';

export type GeoLiveUpdate = {
  messageMedia: MessageMedia.messageMediaGeoLive,
  date: number,
  editDate?: number
};

export type WrapGeoResult = {
  canHaveTail: boolean | undefined,
  mediaRequiresMessageDiv: boolean | undefined,
  isVenue: boolean,
  isLive: boolean,
  isLiveExpired: boolean,
  footer?: HTMLElement,
  title?: HTMLElement,
  address?: HTMLElement,
  update?: (data?: GeoLiveUpdate, isExpired?: boolean) => void
};

export default function wrapGeo({
  messageMedia,
  attachmentDiv,
  wrapOptions,
  peerId,
  date,
  editDate,
  middleware,
  loadPromises,
  onLiveExpire
}: {
  messageMedia: MessageMedia.messageMediaVenue | MessageMedia.messageMediaGeo | MessageMedia.messageMediaGeoLive,
  attachmentDiv: HTMLElement,
  wrapOptions: WrapSomethingOptions,
  peerId?: PeerId,
  date: number,
  editDate?: number,
  middleware: Middleware,
  loadPromises: Promise<any>[],
  onLiveExpire?: (footer: HTMLElement) => void
}): WrapGeoResult {
  const container = document.createElement('a');
  container.classList.add('geo-container', 'shimmer-bright');

  const isVenue = messageMedia._ === 'messageMediaVenue';
  const isLive = messageMedia._ === 'messageMediaGeoLive';
  const {geo} = messageMedia;
  assumeType<GeoPoint.geoPoint>(geo);

  const svgWidth = 277;
  const svgHeight = 195;

  container.innerHTML = `
  <svg class="geo-svg" width="${svgWidth}px" height="${svgHeight}px" viewBox="0 0 277 195" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <g id="Artboard" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
      <path class="geo-svg-path" d="M274.275109,103.333333 C275.780025,103.333333 277,104.567636 277,106.090226 L277,195 L201.724891,195 C200.219975,195 199,193.765697 199,192.243108 L199,109.536341 C199,106.110514 201.744944,103.333333 205.131004,103.333333 L274.275109,103.333333 Z M71.8689956,103.333333 C75.2550558,103.333333 78,106.110514 78,109.536341 L78,192.243108 C78,193.765697 76.7800248,195 75.2751092,195 L3.05533376e-13,195 L3.05533376e-13,106.090226 C3.05533376e-13,104.567636 1.21997518,103.333333 2.72489083,103.333333 L71.8689956,103.333333 Z M179.467331,145 C185.100269,145 190,149.937497 190,155.617021 L190,192.257683 C190,193.772223 188.448961,195 186.946844,195 L90.0531561,195 C88.5510395,195 87,193.772223 87,192.257683 L87,155.617021 C87,149.937497 91.8997314,145 97.5326689,145 L179.467331,145 Z M141.438333,165.734879 L136.933333,167.553888 L136.933333,166.559118 C136.933333,166.237002 136.805833,166.000152 136.550833,165.848568 C136.295833,165.696983 136.026667,165.678035 135.743333,165.791724 L130.02,168.236016 C129.698889,168.368652 129.448611,168.581817 129.269167,168.875512 C129.089722,169.169206 129,169.486585 129,169.827649 L129,180.628013 C129,181.101713 129.165278,181.504358 129.495833,181.835948 C129.826389,182.167538 130.227778,182.333333 130.7,182.333333 L149.966667,182.333333 C150.438889,182.333333 150.840278,182.167538 151.170833,181.835948 C151.501389,181.504358 151.666667,181.101713 151.666667,180.628013 L151.666667,168.690769 L142.6,168.690769 L142.6,166.530696 C142.6,166.227528 142.477222,165.990678 142.231667,165.820146 C141.986111,165.649613 141.721667,165.621191 141.438333,165.734879 Z M140.5,173.333333 C140.740741,173.333333 140.939815,173.410069 141.097222,173.563542 C141.25463,173.717014 141.333333,173.911111 141.333333,174.145833 L141.333333,176.854167 C141.333333,177.088889 141.25463,177.282986 141.097222,177.436458 C140.939815,177.589931 140.740741,177.666667 140.5,177.666667 C140.259259,177.666667 140.060185,177.589931 139.902778,177.436458 C139.74537,177.282986 139.666667,177.088889 139.666667,176.854167 L139.666667,174.145833 C139.666667,173.911111 139.74537,173.717014 139.902778,173.563542 C140.060185,173.410069 140.259259,173.333333 140.5,173.333333 Z M135.833333,173.333333 C136.074074,173.333333 136.273148,173.410069 136.430556,173.563542 C136.587963,173.717014 136.666667,173.911111 136.666667,174.145833 L136.666667,176.854167 C136.666667,177.088889 136.587963,177.282986 136.430556,177.436458 C136.273148,177.589931 136.074074,177.666667 135.833333,177.666667 C135.592593,177.666667 135.393519,177.589931 135.236111,177.436458 C135.078704,177.282986 135,177.088889 135,176.854167 L135,174.145833 C135,173.911111 135.078704,173.717014 135.236111,173.563542 C135.393519,173.410069 135.592593,173.333333 135.833333,173.333333 Z M144.833333,173.333333 C145.074074,173.333333 145.273148,173.410069 145.430556,173.563542 C145.587963,173.717014 145.666667,173.911111 145.666667,174.145833 L145.666667,176.854167 C145.666667,177.088889 145.587963,177.282986 145.430556,177.436458 C145.273148,177.589931 145.074074,177.666667 144.833333,177.666667 C144.592593,177.666667 144.393519,177.589931 144.236111,177.436458 C144.078704,177.282986 144,177.088889 144,176.854167 L144,174.145833 C144,173.911111 144.078704,173.717014 144.236111,173.563542 C144.393519,173.410069 144.592593,173.333333 144.833333,173.333333 Z M149.658824,159.666667 L148.341176,159.666667 C148.139869,159.666667 147.966013,159.735119 147.819608,159.872024 C147.673203,160.008929 147.581699,160.177778 147.545098,160.378571 L146.666667,167.333333 L151.333333,167.333333 L150.454902,160.378571 C150.418301,160.177778 150.326797,160.008929 150.180392,159.872024 C150.033987,159.735119 149.860131,159.666667 149.658824,159.666667 Z M249.919094,140.666667 L238.414239,140.666667 C237.928803,140.666667 237.516181,140.839506 237.176375,141.185185 C236.83657,141.530864 236.666667,141.950617 236.666667,142.444444 L236.666667,144.518519 L242.812298,148.903704 C243.02589,149.061728 243.200647,149.298765 243.33657,149.614815 C243.472492,149.930864 243.540453,150.237037 243.540453,150.533333 L243.540453,162 L249.919094,162 C250.404531,162 250.817152,161.82716 251.156958,161.481481 C251.496764,161.135802 251.666667,160.716049 251.666667,160.222222 L251.666667,142.444444 C251.666667,141.950617 251.496764,141.530864 251.156958,141.185185 C250.817152,140.839506 250.404531,140.666667 249.919094,140.666667 Z M233.681454,145 C233.306839,145 232.961799,145.109357 232.646334,145.32807 L226.406038,149.831579 C226.169439,149.990643 225.987061,150.204386 225.858903,150.472807 C225.730746,150.741228 225.666667,151.01462 225.666667,151.292982 L225.666667,161.105263 C225.666667,161.363743 225.750462,161.577485 225.918053,161.746491 C226.085644,161.915497 226.297597,162 226.553913,162 L231.315465,162 L231.315465,155.408772 L236.047443,155.408772 L236.047443,162 L240.779421,162 C241.035736,162 241.247689,161.915497 241.41528,161.746491 C241.582871,161.577485 241.666667,161.363743 241.666667,161.105263 L241.666667,151.292982 C241.666667,151.01462 241.602588,150.741228 241.47443,150.472807 C241.346272,150.204386 241.163894,149.990643 240.927295,149.831579 L234.716574,145.32807 C234.401109,145.109357 234.056069,145 233.681454,145 Z M28.1107355,151 C26.7011204,151.535116 25.6080384,152.177255 24.8314897,152.926418 C24.054941,153.67558 23.6666667,154.507839 23.6666667,155.423195 C23.6666667,156.313825 24.0383266,157.128954 24.7816465,157.868583 C25.5249664,158.608212 26.5730748,159.243985 27.9259717,159.775903 L28.3614887,159.946655 C29.6189312,160.436878 31.0759356,160.969375 32.7325021,161.229902 C34.5839587,161.521079 36.6177601,161.666667 38.8339062,161.666667 C41.0575639,161.666667 43.0932432,161.521079 44.940944,161.229902 C46.7886449,160.938725 48.3905989,160.30782 49.746806,159.775903 C51.1030131,159.243985 52.1509305,158.608212 52.8905583,157.868583 C53.6301861,157.128954 54,156.313825 54,155.423195 C54,154.74612 53.7747506,154.109424 53.3242518,153.513108 C52.8737529,152.916792 52.232252,152.376601 51.399749,151.892537 C51.0481091,152.042615 50.6745076,152.157696 50.2789446,152.237779 C49.8833815,152.317862 49.4585364,152.366145 49.0044091,152.382629 C48.0792219,153.944799 46.7901408,155.168707 45.137166,156.054355 C43.4841911,156.940003 41.5781488,157.382827 39.4190391,157.382827 L38.373477,157.382827 C36.7571047,157.382827 35.2762577,157.12145 33.9309359,156.598697 C32.5856141,156.075944 31.4161441,155.337853 30.4225259,154.384424 C29.4289077,153.430995 28.6583108,152.302854 28.1107355,151 Z M246.666667,155.666667 L246.666667,157.333333 L245,157.333333 L245,155.666667 L246.666667,155.666667 Z M38.9247583,138.333333 C36.8805223,138.333333 35.0878837,138.524784 33.5468423,138.907687 C32.005801,139.290589 30.807178,139.825485 29.9509735,140.512377 C29.0947689,141.199268 28.6666667,142.000558 28.6666667,142.916248 L28.6666667,145.217011 C28.6666667,146.65308 28.8977785,147.968639 29.3600021,149.163689 C29.8222257,150.358738 30.4832473,151.391681 31.343067,152.262516 C32.2028866,153.133352 33.2290636,153.807308 34.421598,154.284385 C35.6141324,154.761462 36.9411543,155 38.4026637,155 L39.4443793,155 C41.3903102,155 43.0928254,154.597401 44.5519248,153.792202 C46.0110242,152.987003 47.1473979,151.850115 47.961046,150.381537 C48.0380117,150.24262 48.1113372,150.101439 48.1810225,149.957994 L48.8310449,149.957752 C50.1301926,149.957752 51.1498054,149.643464 51.8898832,149.014889 C52.6299611,148.386314 53,147.516781 53,146.406291 C53,145.303865 52.6297708,144.436566 51.8893124,143.804392 C51.2166514,143.230102 50.3137723,142.916665 49.1810488,142.863979 C49.1649624,141.970034 48.735209,141.186167 47.8917885,140.512377 C47.0319689,139.825485 45.8315067,139.290589 44.2904019,138.907687 C42.7492971,138.524784 40.9607493,138.333333 38.9247583,138.333333 Z M246.666667,150.333333 L246.666667,152 L245,152 L245,150.333333 L246.666667,150.333333 Z M49.3462947,144.666667 C49.9216674,144.707967 50.3794206,144.850985 50.719554,145.095721 C51.1287402,145.390142 51.3333333,145.798671 51.3333333,146.321307 C51.3333333,146.844067 51.1285423,147.254623 50.7189604,147.552974 C50.312817,147.848821 49.7398788,147.997986 49,148 C49.2305654,147.116215 49.3458481,146.164212 49.3458481,145.143993 L49.3462947,144.666667 Z M246.666667,145.333333 L246.666667,147 L245,147 L245,145.333333 L246.666667,145.333333 Z M39.0006663,140.333333 C40.5947307,140.333333 42.0207584,140.451406 43.2787493,140.687551 C44.5367403,140.923696 45.527002,141.243793 46.2495345,141.647842 C46.9720671,142.051891 47.3333333,142.502395 47.3333333,142.999356 C47.3333333,143.49558 46.9718767,143.9459 46.2489634,144.350317 C45.5260501,144.754734 44.5357884,145.07523 43.2781782,145.311805 C42.020568,145.548379 40.5947307,145.666667 39.0006663,145.666667 C37.406475,145.666667 35.9822242,145.548379 34.7279138,145.311805 C33.4736034,145.07523 32.4831513,144.754734 31.7565574,144.350317 C31.0299636,143.9459 30.6666667,143.49558 30.6666667,142.999356 C30.6666667,142.502395 31.0297732,142.051891 31.7559863,141.647842 C32.4821994,141.243793 33.4724611,140.923696 34.7267715,140.687551 C35.9810819,140.451406 37.4057135,140.333333 39.0006663,140.333333 Z M176.83043,52 C183.919688,52 190,58.1245592 190,65.2683983 L190,122.398268 C190,129.542107 183.919688,135 176.83043,135 L100.16957,135 C93.0803124,135 87,129.542107 87,122.398268 L87,65.2683983 C87,58.1245592 93.0803124,52 100.16957,52 L176.83043,52 Z M138.333333,80.3333333 C135.511111,80.3333333 133.027778,81.3372814 130.883333,83.3451777 C128.738889,85.3530739 127.666667,88.0490694 127.666667,91.4331641 C127.666667,93.6215454 128.488889,95.9791314 130.133333,98.5059222 C131.777778,101.032713 134.255556,103.77383 137.566667,106.729272 C137.677778,106.819515 137.8,106.887197 137.933333,106.932318 C138.066667,106.977439 138.211111,107 138.366667,107 C138.5,107 138.627778,106.977439 138.75,106.932318 C138.872222,106.887197 138.988889,106.819515 139.1,106.729272 C142.411111,103.77383 144.888889,101.032713 146.533333,98.5059222 C148.177778,95.9791314 149,93.6215454 149,91.4331641 C149,88.0490694 147.927778,85.3530739 145.783333,83.3451777 C143.638889,81.3372814 141.155556,80.3333333 138.333333,80.3333333 Z M138,88 C138.920635,88 139.706349,88.3253968 140.357143,88.9761905 C141.007937,89.6269841 141.333333,90.4126984 141.333333,91.3333333 C141.333333,92.2539683 141.007937,93.0396825 140.357143,93.6904762 C139.706349,94.3412698 138.920635,94.6666667 138,94.6666667 C137.079365,94.6666667 136.293651,94.3412698 135.642857,93.6904762 C134.992063,93.0396825 134.666667,92.2539683 134.666667,91.3333333 C134.666667,90.4126984 134.992063,89.6269841 135.642857,88.9761905 C136.293651,88.3253968 137.079365,88 138,88 Z M277,0 L277,91.2454212 C277,92.7667331 275.781665,94 274.278772,94 L205.227621,94 C201.846113,94 199.104859,91.2251494 199.104859,87.8021978 L199.104859,66.7985348 C199.104859,54.8182041 189.51047,41.6630037 177.675192,41.6630037 L149.102302,41.6630037 C146.284378,41.6630037 144,39.3506282 144,36.4981685 L144,2.75457875 C144,1.23326692 145.218335,0 146.721228,0 L277,0 Z M129.945299,0 C131.448269,0 132.666667,1.23326692 132.666667,2.75457875 L132.666667,36.4981685 C132.666667,39.3506282 130.382171,41.6630037 127.564103,41.6630037 L99.3299145,41.6630037 C87.4940275,41.6630037 77.8991453,54.8182041 77.8991453,66.7985348 L77.8991453,87.8021978 C77.8991453,91.2251494 75.1577504,94 71.7760684,94 L2.72136752,94 C1.21839774,94 0,92.7667331 0,91.2454212 L0,0 L129.945299,0 Z M237.333333,28.6666667 C235.486111,28.6666667 233.875868,29.2909326 232.502604,30.5394645 C231.12934,31.7879963 230.333333,33.3393899 230.114583,35.1936451 C228.753472,35.7622834 227.671875,36.6708685 226.869792,37.9194003 C226.067708,39.1679322 225.666667,40.5339002 225.666667,42.0173044 C225.666667,44.0693469 226.377604,45.8185276 227.799479,47.2648467 C229.221354,48.7111658 230.940972,49.4343254 232.958333,49.4343254 L236.239583,49.4343254 L236.239583,55.0331916 L232.958333,55.0331916 C232.642361,55.0331916 232.381076,55.1382661 232.174479,55.348415 C231.967882,55.5585639 231.864583,56.5660426 231.864583,56.8874469 C231.864583,57.2088511 231.967882,57.4746277 232.174479,57.6847766 C232.381076,57.8949255 232.642361,58 232.958333,58 L241.34375,58 C241.659722,58 241.921007,57.8949255 242.127604,57.6847766 C242.334201,57.4746277 242.4375,57.2088511 242.4375,56.8874469 C242.4375,56.5660426 242.334201,55.5585639 242.127604,55.348415 C241.921007,55.1382661 241.659722,55.0331916 241.34375,55.0331916 L238.427083,55.0331916 L238.427083,49.4343254 L241.708333,49.4343254 C243.725694,49.4343254 245.445312,48.7111658 246.867188,47.2648467 C248.289062,45.8185276 249,44.0693469 249,42.0173044 C249,40.5339002 248.598958,39.1679322 247.796875,37.9194003 C246.994792,36.6708685 245.913194,35.7622834 244.552083,35.1936451 C244.333333,33.3393899 243.537326,31.7879963 242.164062,30.5394645 C240.790799,29.2909326 239.180556,28.6666667 237.333333,28.6666667 Z M39.3280935,33 C39.2243891,33 39.1258699,33.0154494 39.0325359,33.0463483 C38.9392019,33.0772472 38.8510531,33.1235955 38.7680896,33.1853933 L31.8613745,38.3405982 L31.8613745,37.6966292 L31.8583795,37.640264 C31.8289402,37.347262 31.5754523,36.7755434 30.7901174,36.7755434 L30.6207017,36.7767977 L30.4578933,36.7810704 C29.9778294,36.7994331 29.824306,36.8638758 29.6931536,36.9792263 L29.6369144,37.0323034 C29.4606169,37.207397 29.3724681,37.428839 29.3724681,37.6966292 L29.3724681,40.2921348 L26.3857805,42.5477528 C26.1783716,42.7125468 26.0539263,42.9185393 26.0124445,43.1657303 C25.9709628,43.4129213 26.0331854,43.6395131 26.1991125,43.8455056 C26.3650396,44.0514981 26.5724485,44.1750936 26.8213391,44.2162921 C27.0702297,44.2574906 27.2983795,44.1956929 27.5057883,44.0308989 L29.3724681,42.6095506 L29.3724681,54.0730337 C29.3724681,54.340824 29.4606169,54.5622659 29.6369144,54.7373596 C29.8132119,54.9124532 30.0361765,55 30.305808,55 L36.866591,55 C37.3669257,55 37.7725271,54.5921805 37.7725271,54.0891096 L37.7725271,49.1292135 C37.7725271,48.8614232 37.8606758,48.6399813 38.0369734,48.4648876 C38.2132709,48.289794 39.058462,47.9008773 39.3280935,47.9008773 C39.5977251,47.9008773 40.4429162,48.289794 40.6192137,48.4648876 C40.7955112,48.6399813 40.88366,48.8614232 40.88366,49.1292135 L40.88366,54.0891096 C40.88366,54.5921805 41.2892614,55 41.7895961,55 L48.3503791,55 C48.6200106,55 48.8429751,54.9124532 49.0192726,54.7373596 C49.1955702,54.5622659 49.283719,54.340824 49.283719,54.0730337 L49.283719,42.6095506 L51.2441994,44.0774017 C51.4336674,44.2081039 51.6357355,44.2595506 51.8504036,44.2317416 C52.0889238,44.2008427 52.2911475,44.082397 52.4570746,43.8764045 C52.6230016,43.670412 52.6904095,43.4438202 52.6592982,43.1966292 C52.6281869,42.9494382 52.5089268,42.7434457 52.3015179,42.5786517 L39.8880975,33.1853933 C39.8051339,33.1235955 39.7169851,33.0772472 39.6236512,33.0463483 C39.5303172,33.0154494 39.431798,33 39.3280935,33 Z" id="Shape" fill="#FFFFFF"></path>
    </g>
  </svg>`;
  setBlankToAnchor(container);

  const svgPlaceholder = container.firstElementChild;

  attachmentDiv.append(container);

  const zoom = 16;

  const setAnchorURL = (geo: GeoPoint.geoPoint) => {
    container.href = makeGoogleMapsUrl(geo);
  };

  const width = mediaSizes.isMobile ? svgWidth : 420;

  let wrapTempId = 0;
  const wrapGeo = (
    media: MessageMedia.messageMediaGeo | MessageMedia.messageMediaGeoLive | MessageMedia.messageMediaVenue,
    loadPromises?: Promise<any>[]
  ) => {
    const geo = media.geo as GeoPoint.geoPoint;
    const _wrapTempId = ++wrapTempId;

    const oldImageContainer = container.querySelector('.geo-image-container');
    if(oldImageContainer) {
      if(liteMode.isAvailable('animations')) {
        oldImageContainer.classList.add('fade-out');
      } else {
        oldImageContainer.remove();
      }
    }

    container.classList.add('shimmer');
    container.prepend(svgPlaceholder);

    const newWrapOptions: WrapSomethingOptions = {
      ...wrapOptions,
      ...(_wrapTempId !== 0 ? {lazyLoadQueue: undefined} : {})
    };

    const imageContainer = document.createElement('div');
    imageContainer.classList.add('geo-image-container');
    if(isLive) {
      const pin = document.createElement('div');
      pin.classList.add('geo-live-pin');

      const heading = document.createElement('div');
      heading.classList.add('geo-live-pin-heading');
      pin.append(heading);

      pin.insertAdjacentHTML('beforeend', `
        <svg version="1.1" class="geo-live-pin-svg" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 64 64" style="enable-background:new 0 0 64 64;" xml:space="preserve">
          <g>
            <circle cx="32" cy="32" r="24.5"/>
            <path d="M32,8c13.23,0,24,10.77,24,24S45.23,56,32,56S8,45.23,8,32S18.77,8,32,8 M32,7C18.19,7,7,18.19,7,32s11.19,25,25,25 s25-11.19,25-25S45.81,7,32,7L32,7z"/>
          </g>
          <g>
            <polygon points="29.38,57.67 27.4,56.08 30.42,54.42 32,51.54 33.58,54.42 36.6,56.08 34.69,57.61 32,60.73"></polygon>
            <path d="M32,52.58l1.07,1.95l0.14,0.26l0.26,0.14l2.24,1.22l-1.33,1.06l-0.07,0.06l-0.06,0.07L32,59.96l-2.24-2.61l-0.06-0.07 l-0.07-0.06l-1.33-1.06l2.24-1.22l0.26-0.14l0.14-0.26L32,52.58 M32,50.5l-1.94,3.56L26.5,56l2.5,2l3,3.5l3-3.5l2.5-2l-3.56-1.94 L32,50.5L32,50.5z"/>
          </g>
        </svg>
      `);

      const avatar = avatarNew({
        middleware,
        size: 54,
        peerId,
        isDialog: false,
        wrapOptions: newWrapOptions
      });
      avatar.node.classList.add('geo-live-pin-avatar');
      loadPromises?.push(avatar.readyThumbPromise);

      pin.append(avatar.node);

      imageContainer.append(pin);
    } else {
      imageContainer.append(GeoPin());
    }

    wrapPhoto({
      photo: getWebFileLocation(geo, width, width / (svgWidth / svgHeight), zoom),
      container: imageContainer,
      fadeInElement: imageContainer,
      onRender: () => {
        if(_wrapTempId !== wrapTempId) return;
        container.append(imageContainer);
      },
      onRenderFinish: () => {
        if(_wrapTempId !== wrapTempId) return;
        container.classList.remove('shimmer');
        svgPlaceholder.remove();
        oldImageContainer?.remove();
      },
      loadPromises,
      ...newWrapOptions
    });
  };

  function getMetersPerPixel(lat: number, zoom: number) {
    // https://groups.google.com/g/google-maps-js-api-v3/c/hDRO4oHVSeM/m/osOYQYXg2oUJ
    return (156543.03392 * Math.cos(lat * (Math.PI / 180))) / 2 ** zoom;
  }

  setAnchorURL(geo);
  wrapGeo(messageMedia, loadPromises);

  let liveExpiration = isLive ? (date + (messageMedia as MessageMedia.messageMediaGeoLive).period) * 1000 : undefined;
  const isLiveExpired = isLive && Date.now() >= liveExpiration;

  const ret: WrapGeoResult = {
    canHaveTail: undefined,
    mediaRequiresMessageDiv: undefined,
    isVenue,
    isLive,
    isLiveExpired
  };

  let footer: HTMLElement, title: HTMLElement, address: HTMLElement;
  if(isVenue || (isLive && !isLiveExpired)) {
    footer = document.createElement('div');
    footer.classList.add('geo-footer');

    title = document.createElement('div');
    title.classList.add('geo-footer-title');

    address = document.createElement('div');
    address.classList.add('geo-footer-address');

    footer.append(title, address);
    ret.mediaRequiresMessageDiv = true;
    ret.footer = footer;
    ret.title = title;
    ret.address = address;
  } else {
    ret.canHaveTail = false;
  }

  if(isLive) {
    container.classList.add('is-live');
    container.classList.toggle('is-expired', isLiveExpired);
  }

  if(isVenue) {
    title.append(wrapEmojiText((messageMedia as MessageMedia.messageMediaVenue).title));
    address.append(wrapEmojiText((messageMedia as MessageMedia.messageMediaVenue).address));
  } else if(isLive && !isLiveExpired) {
    title.classList.add('disable-hover');
    address.classList.add('disable-hover');
    footer.classList.add('is-live');

    const updatedI18n = new I18n.IntlElement();
    title.append(i18n('AttachLiveLocation'));
    address.append(updatedI18n.element);

    const timer = document.createElement('div');
    timer.classList.add('geo-footer-timer');

    const radius = 13;
    const size = (radius + 2) * 2;
    const circumference = radius * 2 * Math.PI;

    const timerTextI18n = new I18n.IntlElement();
    timer.append(timerTextI18n.element);
    timer.insertAdjacentHTML('beforeend', `
      <svg class="geo-footer-timer-svg" width="${size}px" height="${size}px">
        <circle cx="${size / 2}" cy="${size / 2}" r="${radius}" class="geo-footer-timer-circle" transform="rotate(-90, ${size / 2}, ${size / 2})" stroke-dasharray="${circumference} ${circumference}"></circle>
      </svg>
    `);
    const timerCircle = timer.lastElementChild.firstElementChild as SVGCircleElement;
    const timerShadowCircle = timerCircle.cloneNode(true) as SVGCircleElement;
    timerShadowCircle.classList.add('geo-footer-timer-circle-shadow');
    timer.lastElementChild.append(timerShadowCircle);

    footer.append(timer);

    let lastData: GeoLiveUpdate = {
      messageMedia: messageMedia as MessageMedia.messageMediaGeoLive,
      date,
      editDate
    };
    let cleaned = false;
    const update = (data: GeoLiveUpdate = lastData, isExpired?: boolean) => {
      if(cleaned) {
        return;
      }

      const {messageMedia: newMedia, date: newDate, editDate: newEditDate} = data;
      const {period} = newMedia;
      const newGeo = newMedia.geo as GeoPoint.geoPoint;
      liveExpiration = isExpired ? 0 : (newDate + period) * 1000;

      if(lastData !== data) {
        if(geo.lat !== newGeo.lat || geo.long !== newGeo.long) {
          setAnchorURL(newGeo);
          wrapGeo(newMedia);
        }

        lastData = data;
      }

      if(Date.now() >= liveExpiration) {
        container.classList.add('is-expired');
        onLiveExpire?.(footer);
        clean();
        return;
      }

      container.style.setProperty('--heading', `${newMedia.heading}deg`);

      if(newGeo.accuracy_radius !== undefined) {
        const accuracyRadiusPx = newGeo.accuracy_radius / getMetersPerPixel(newGeo.lat, zoom);
        container.style.setProperty('--accuracy-size', `${accuracyRadiusPx * 2}px`);
      }

      let langPackKey: LangPackKey, langPackArgs: FormatterArguments;
      if((tsNow(true) - (newEditDate ?? newDate)) < 60) {
        langPackKey = 'LocationUpdatedJustNow';
      } else {
        langPackKey = 'UpdatedMinutes';
        langPackArgs = [Math.floor((tsNow(true) - newEditDate) / 60)];
      }

      const timeLeft = (liveExpiration - Date.now()) / 1000;
      const strokeDashOffset = (1 - timeLeft / period) * circumference;
      timerCircle.setAttribute('stroke-dashoffset', `-${strokeDashOffset}`);

      updatedI18n.compareAndUpdate({
        key: langPackKey,
        args: langPackArgs
      });

      timerTextI18n.compareAndUpdate({
        key: timeLeft < 3600 ? 'JustArgument' : 'MessageTimer.ShortHours',
        args: [Math.round(timeLeft < 3600 ? timeLeft / 60 : timeLeft / 3600)]
      });
    };

    const interval = setInterval(update, 1000);
    update();

    const clean = () => {
      cleaned = true;
      clearInterval(interval);
    };

    middleware.onClean(clean);

    ret.update = update;
  }

  return ret;
}
